// -----------------------------------------------------------------------------
// src/features/reports/reportThunks.js - WITH DATE FILTERING AND COMMENTS
// -----------------------------------------------------------------------------
import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../api/axiosInstance';

/* ───────────────────────── Report thunks ───────────────────── */

export const fetchReportsThunk = createAsyncThunk(
  'reports/fetchAll',
  async ({ 
    offset = 0, 
    limit = 20, 
    store_id = null, 
    status = null, 
    employee_id = null,
    severity = null,      
    search = null,
    start_date = null,
    end_date = null
  } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ limit, offset });
      
      if (store_id) params.append('store_id', store_id);
      if (status) params.append('status', status);
      if (employee_id) params.append('employee_id', employee_id);
      if (severity) params.append('severity', severity);        
      if (search) params.append('search', search);              
      if (start_date) params.append('start_date', start_date);
      if (end_date) params.append('end_date', end_date);
      
      console.log('Sending request with params:', params.toString());
      
      const { data } = await axiosInstance.get(`/api/reports/?${params}`);
      return data;
    } catch (error) {
      console.error('Fetch reports error:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch reports');
    }
  }
);

export const fetchMonthlyStatsThunk = createAsyncThunk(
  'reports/fetchMonthlyStats',
  async ({ 
    store_id = null, 
    start_date = null,
    end_date = null
  } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      
      if (store_id) params.append('store_id', store_id);
      if (start_date) params.append('start_date', start_date);
      if (end_date) params.append('end_date', end_date);
      
      console.log('Fetching monthly stats with params:', params.toString());
      
      const { data } = await axiosInstance.get(`/api/reports/monthly-stats/${params.toString() ? `?${params}` : ''}`);
      return data;
    } catch (error) {
      console.error('Fetch monthly stats error:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch monthly report statistics');
    }
  }
);

export const fetchReportThunkById = createAsyncThunk(
  'reports/fetchOne',
  async (reportId, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get(`/api/reports/${reportId}/`);
      return data;
    } catch (error) {
      console.error('Fetch report by ID error:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch report');
    }
  }
);

export const createReportThunk = createAsyncThunk(
  'reports/create',
  async (reportData, { rejectWithValue }) => {
    try {
      console.log('Creating report with data:', reportData);
      
      const isFormData = reportData instanceof FormData;
      
      const config = {
        headers: {}
      };
      
      if (!isFormData) {
        config.headers['Content-Type'] = 'application/json';
      }

      if (isFormData) {
        console.log('FormData contents (create):');
        for (let [key, value] of reportData.entries()) {
          console.log(key, value);
        }
      }
      
      const { data } = await axiosInstance.post('/api/reports/', reportData, config);
      console.log('Report created successfully:', data);
      return data;
    } catch (error) {
      console.error('Create report error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      if (error.response?.data) {
        return rejectWithValue({
          message: error.response.data.message || 'Failed to create report',
          details: error.response.data,
          status: error.response.status
        });
      }
      return rejectWithValue({ message: 'Failed to create report' });
    }
  }
);

export const updateReportThunk = createAsyncThunk(
  'reports/update',
  async ({ reportId, updates }, { rejectWithValue }) => {
    try {
      console.log('Updating report ID:', reportId);
      console.log('Update data type:', updates instanceof FormData ? 'FormData' : 'JSON');
      
      const isFormData = updates instanceof FormData;
      
      const config = {
        headers: {}
      };
      
      if (!isFormData) {
        config.headers['Content-Type'] = 'application/json';
      }

      if (isFormData) {
        console.log('FormData contents (update):');
        for (let [key, value] of updates.entries()) {
          console.log(key, value);
        }
      }
      
      console.log('Making PATCH request to:', `/api/reports/${reportId}/`);
      const { data } = await axiosInstance.patch(`/api/reports/${reportId}/`, updates, config);
      
      console.log('Report updated successfully:', data);
      return data;
    } catch (error) {
      console.error('Update report error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error headers:', error.response?.headers);
      
      if (error.response?.data) {
        return rejectWithValue({
          message: error.response.data.message || 'Failed to update report',
          details: error.response.data,
          status: error.response.status
        });
      }
      return rejectWithValue({ message: 'Failed to update report' });
    }
  }
);

export const fetchIncidentTypeCountsThunk = createAsyncThunk(
  'reports/fetchIncidentTypeCounts',
  async ({ 
    severity = null, 
    store_id = null, 
    start_date = null,
    end_date = null
  } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (severity) params.append('severity', severity);
      if (store_id) params.append('store_id', store_id);
      if (start_date) params.append('start_date', start_date);
      if (end_date) params.append('end_date', end_date);
      
      const { data } = await axiosInstance.get(`/api/reports/incident-type-counts/${params.toString() ? `?${params}` : ''}`);
      return data;
    } catch (error) {
      console.error('Fetch incident type counts error:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch incident type counts');
    }
  }
);

export const fetchTopEmployeesThunk = createAsyncThunk(
  'reports/fetchTopEmployees',
  async ({ 
    severity = null, 
    store_id = null, 
    start_date = null,
    end_date = null
  } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (severity) params.append('severity', severity);
      if (store_id) params.append('store_id', store_id);
      if (start_date) params.append('start_date', start_date);
      if (end_date) params.append('end_date', end_date);
      
      const { data } = await axiosInstance.get(`/api/reports/top-employees/${params.toString() ? `?${params}` : ''}`);
      return data;
    } catch (error) {
      console.error('Fetch top employees error:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch top employees');
    }
  }
);

export const fetchIncidentTypeDailyThunk = createAsyncThunk(
  'reports/fetchIncidentTypeDaily',
  async ({ 
    employee = null, 
    store_id = null, 
    start_date = null,
    end_date = null
  } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (employee) params.append('employee', employee);
      if (store_id) params.append('store_id', store_id);
      if (start_date) params.append('start_date', start_date);
      if (end_date) params.append('end_date', end_date);
      
      const { data } = await axiosInstance.get(`/api/reports/incident-type-daily/${params.toString() ? `?${params}` : ''}`);
      return data;
    } catch (error) {
      console.error('Fetch incident type daily error:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch daily incident type data');
    }
  }
);

export const fetchReportsByDateRangeThunk = createAsyncThunk(
  'reports/fetchByDateRange',
  async ({ 
    start_date, 
    end_date, 
    store_id = null, 
    employee_id = null,
    limit = 100,
    offset = 0
  }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ 
        start_date, 
        end_date, 
        limit, 
        offset 
      });
      
      if (store_id) params.append('store_id', store_id);
      if (employee_id) params.append('employee_id', employee_id);
      
      console.log('Fetching reports by date range:', params.toString());
      
      const { data } = await axiosInstance.get(`/api/reports/by-date-range/?${params}`);
      return data;
    } catch (error) {
      console.error('Fetch reports by date range error:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch reports by date range');
    }
  }
);

export const fetchDateRangeStatsThunk = createAsyncThunk(
  'reports/fetchDateRangeStats',
  async ({ 
    start_date, 
    end_date, 
    store_id = null 
  }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ 
        start_date, 
        end_date 
      });
      
      if (store_id) params.append('store_id', store_id);
      
      console.log('Fetching date range stats:', params.toString());
      
      const { data } = await axiosInstance.get(`/api/reports/summary/?${params}`);
      return data;
    } catch (error) {
      console.error('Fetch date range stats error:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch date range statistics');
    }
  }
);

/* ───────────────────────── Comment thunks ───────────────────── */

export const fetchReportCommentsThunk = createAsyncThunk(
  'reports/fetchComments',
  async (reportId, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get(`/api/reports/${reportId}/comments/`);
      return data;
    } catch (error) {
      console.error('Fetch comments error:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch comments');
    }
  }
);

export const addReportCommentThunk = createAsyncThunk(
  'reports/addComment',
  async ({ reportId, text }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post(`/api/reports/${reportId}/add_comment/`, { text });
      return { reportId, comment: data };
    } catch (error) {
      console.error('Add comment error:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to add comment');
    }
  }
);